import React, { useEffect, useState, useMemo, useCallback } from "react";
import ReactHTMLTableToExcel from "react-html-table-to-excel";
import { formatAmount, getAllMonths, getAllYears, getFieldData } from "../../Services/appService";
import GeneratePdf from "../../app/components/Report/GeneratePdf";
import { getCurrentUser } from "../../app/store/auth";
import { useGetUserDetailsQuery } from "../../app/store/authApiSlice";
import { useLoadPartnerAllCandidateQuery } from "../../app/store/partnerApiSlice";
import { useLoadCandidatesByFilterMutation } from "../../app/store/candidateApiSlice";
import { useLoadMonthlyPayCandidatesQuery } from "../../app/store/reportApiSlice";

const CUSTOM_REPORT_STYLES = {
  pageWrap: {
    padding: "8px 0 4px 0"
  },
  shell: {
    borderRadius: "20px",
    border: "1px solid rgba(226, 232, 240, 0.95)",
    backgroundColor: "#ffffff",
    boxShadow: "0 10px 30px rgba(15, 23, 42, 0.05)",
    overflow: "hidden"
  },
  hero: {
    padding: "18px 22px",
    borderBottom: "1px solid rgba(226, 232, 240, 0.9)",
    background: "linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)"
  },
  heroRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
    flexWrap: "wrap"
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flex: "1"
  },
  titleIcon: {
    width: "34px",
    height: "34px",
    borderRadius: "10px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#ffffff",
    color: "#2563eb",
    fontSize: "14px",
    border: "1px solid rgba(191, 219, 254, 0.9)",
    boxShadow: "0 4px 12px rgba(37, 99, 235, 0.06)",
    flexShrink: 0
  },
  titleText: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    minWidth: 0
  },
  title: {
    margin: 0,
    color: "#0f172a",
    fontSize: "18px",
    fontWeight: 600,
    lineHeight: 1.25
  },
  subtitle: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#64748b",
    margin: "2px 0 0 0"
  },
  actionRow: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "flex-end"
  },
  reportActionButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    borderRadius: "8px",
    border: "1px solid #cbd5e1",
    backgroundColor: "#ffffff",
    background: "#ffffff",
    color: "#334155",
    padding: "9px 18px",
    fontSize: "13px",
    fontWeight: 600,
    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.04)",
    cursor: "pointer",
    transition: "all 0.2s ease",
    lineHeight: 1.5,
    margin: 0
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "14px",
    padding: "20px 22px"
  },
  statCard: {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
    backgroundColor: "#ffffff",
    border: "1px solid rgba(226, 232, 240, 0.95)",
    borderRadius: "16px",
    padding: "16px",
    boxShadow: "0 6px 18px rgba(15, 23, 42, 0.03)",
    transition: "all 0.2s ease"
  },
  statIcon: {
    width: "36px",
    height: "36px",
    borderRadius: "10px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#eff6ff",
    color: "#2563eb",
    fontSize: "14px",
    flexShrink: 0
  },
  statContent: {
    minWidth: 0
  },
  statLabel: {
    margin: "0 0 4px 0",
    color: "#64748b",
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase"
  },
  statValue: {
    margin: 0,
    color: "#0f172a",
    fontSize: "24px",
    fontWeight: 600,
    lineHeight: 1
  },
  filterSection: {
    padding: "0 22px 20px 22px"
  },
  filterShell: {
    borderRadius: "14px",
    border: "1px solid rgba(226, 232, 240, 0.95)",
    background: "#f8fafc",
    padding: "16px 20px"
  },
  filterGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "16px"
  },
  filterLabel: {
    display: "block",
    marginBottom: "6px",
    color: "#64748b",
    fontSize: "11px",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.08em"
  },
  selectInput: {
    width: "100%",
    height: "40px",
    padding: "0 12px",
    borderRadius: "8px",
    border: "1px solid #cbd5e1",
    backgroundColor: "#ffffff",
    color: "#0f172a",
    fontSize: "13px",
    fontWeight: 600,
    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.04)",
    outline: "none"
  },
  body: {
    padding: "0"
  },
  tableWrap: {
    overflowX: "auto",
    backgroundColor: "#ffffff"
  },
  table: {
    width: "100%",
    marginBottom: 0,
    borderCollapse: "separate",
    borderSpacing: 0,
    fontSize: "13px",
    color: "#334155"
  },
  tableHeadCell: {
    borderTop: "none",
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#64748b",
    background: "#f8fafc",
    borderBottom: "1px solid rgba(226, 232, 240, 0.9)",
    padding: "14px 16px"
  },
  tableCell: {
    padding: "14px 16px",
    borderBottom: "1px solid rgba(241, 245, 249, 1)",
    verticalAlign: "top",
    fontWeight: 600
  },
  yearBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    padding: "4px 10px",
    borderRadius: "999px",
    backgroundColor: "#eff6ff",
    border: "1px solid rgba(191, 219, 254, 0.9)",
    color: "#2563eb",
    fontSize: "12px",
    fontWeight: 600
  },
  totalCell: {
    verticalAlign: "top",
    padding: "14px 16px",
    borderTop: "2px solid rgba(226, 232, 240, 1)",
    background: "#f8fafc",
    color: "#0f172a",
    fontWeight: 600
  },
  emptyState: {
    padding: "42px 24px",
    textAlign: "center",
    backgroundColor: "#ffffff",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center"
  },
  emptyIcon: {
    width: "64px",
    height: "64px",
    margin: "0 auto 14px auto",
    borderRadius: "18px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #dbeafe 0%, #eff6ff 100%)",
    color: "#2563eb",
    fontSize: "26px"
  },
  emptyTitle: {
    margin: 0,
    color: "#0f172a",
    fontSize: "18px",
    fontWeight: 600,
    textAlign: "center"
  },
  emptyText: {
    margin: "10px auto 0 auto",
    color: "#64748b",
    fontSize: "14px",
    lineHeight: 1.6,
    fontWeight: 600,
    textAlign: "center"
  }
};

const sortReportsByLatestPeriod = (reports = []) =>
  [...reports].sort((a, b) => {
    const yearDiff = Number(b.year || 0) - Number(a.year || 0);
    if (yearDiff !== 0) return yearDiff;
    return Number(b.month || 0) - Number(a.month || 0);
  });

const CustomReport = () => {
  const currentUser = getCurrentUser();
  const currentUserId = currentUser?.linkUser || currentUser?.id;
  const usrType = localStorage.getItem("userType");
  const isCandidateUser = usrType === "candidate";

  const { data: userDetails } = useGetUserDetailsQuery(currentUserId, {
    skip: !currentUserId,
  });

  const partnerId = userDetails?.partnerid;
  const isApprovedPartner = userDetails?.approved > 0 && !isCandidateUser;

  const { data: partnerCandidates } = useLoadPartnerAllCandidateQuery(
    { partId: partnerId, perfId: 0 },
    { skip: !isApprovedPartner || !partnerId }
  );

  const [loadCandidatesByFilter, { data: filteredCandidates, reset: resetCandidates }] =
    useLoadCandidatesByFilterMutation();

  const [selectedCandidateId, setSelectedCandidateId] = useState(() => {
    return isCandidateUser ? (currentUser?.id || "") : "";
  });
  const [selectedYear, setSelectedYear] = useState("");
  const [pdfShowHide, setPdfShowHide] = useState(false);
  const [selectedCol, setSelectedCol] = useState([]);
  const [selectedColData, setSelectedColData] = useState([]);

  const months = useMemo(() => getAllMonths(), []);
  const years = useMemo(() => getAllYears(), []);

  const candidateList = useMemo(() => {
    return partnerCandidates || [];
  }, [partnerCandidates]);

  const getCandidateDetails = useCallback((id) => {
    const frmBody = new FormData();
    frmBody.append("id", id);
    frmBody.append("name", "");
    frmBody.append("email", "");
    frmBody.append("entity", "");
    frmBody.append("type", "candidate");
    frmBody.append("filterType", "single");
    loadCandidatesByFilter(frmBody);
  }, [loadCandidatesByFilter]);

  useEffect(() => {
    if (selectedCandidateId) {
      getCandidateDetails(selectedCandidateId);
    }
  }, [selectedCandidateId, getCandidateDetails]);

  const candidateObj = useMemo(() => {
    if (filteredCandidates && filteredCandidates.length > 0) {
      return filteredCandidates[0];
    }
    return null;
  }, [filteredCandidates]);

  const candidateEntity = useMemo(() => {
    if (!candidateObj) return null;
    return getFieldData(
      candidateObj.performerData,
      "candidateonboardingform",
      "CandidateEntity"
    );
  }, [candidateObj]);

  const reportType = useMemo(() => {
    if (!candidateEntity) return null;
    let rptType = "candidate";
    if (candidateEntity === "C2C Basis") rptType = "subcontractor";
    if (userDetails?.partnerType === "Recruiter") rptType = "recruiter";
    return rptType;
  }, [candidateEntity, userDetails]);

  const { data: monthlyPayCandidates = [] } = useLoadMonthlyPayCandidatesQuery(
    { id: candidateObj?.id, type: reportType },
    { skip: !candidateObj?.id || !reportType }
  );

  const allRpts = useMemo(() => {
    if (!monthlyPayCandidates || monthlyPayCandidates.length === 0) return [];
    return sortReportsByLatestPeriod(monthlyPayCandidates);
  }, [monthlyPayCandidates]);

  const monthlyRpt = useMemo(() => {
    if (selectedYear === "") return allRpts;
    return allRpts.filter((x) => x.year === parseInt(selectedYear, 10));
  }, [allRpts, selectedYear]);

  const handleCandidateChange = useCallback(({ currentTarget }) => {
    const val = currentTarget.value;
    setSelectedCandidateId(val);
    setSelectedYear("");
    if (!val) {
      resetCandidates();
    }
  }, [resetCandidates]);

  const handleChangeYear = useCallback(({ currentTarget }) => {
    setSelectedYear(currentTarget.value);
  }, []);

  const getNetAmountWithAdjustment = useCallback((item) => {
    let netAmt = Number(item?.totalAmount) || 0;

    if (usrType !== "candidate" || candidateEntity === "1099 Basis") {
      if (item?.adjustPlus) netAmt += Number(item.adjustPlus);
      if (item?.adjustMinus) netAmt -= Number(item.adjustMinus);
    }

    if (usrType === "candidate" && item?.adpDeduction) {
      netAmt -= Number(item.adpDeduction);
    }

    return netAmt.toFixed(2);
  }, [usrType, candidateEntity]);

  const getTotalRows = useCallback(() => {
    let hrs = 0;
    let min = 0;
    let invAmt = 0;
    let payAmt = 0;
    let adPlusAmt = 0;
    let adMinAmt = 0;
    let adpAmt = 0;

    monthlyRpt.forEach((item) => {
      hrs += Number(item.hours) || 0;
      min += Number(item.minutes) || 0;
      invAmt += Number(item.totalAmount) || 0;
      payAmt += Number(item.paidAmount) || 0;
      adPlusAmt += Number(item.adjustPlus) || 0;
      adMinAmt += Number(item.adjustMinus) || 0;
      adpAmt += Number(item.adpDeduction) || 0;
    });

    hrs += Math.floor(min / 60);
    min = min % 60;

    const isCandidate = usrType === "candidate";
    const isW2 = isCandidate && candidateEntity === "W2 Basis";
    const is1099 = !isCandidate || candidateEntity === "1099 Basis";

    let adjValue = 0;

    if (isW2) {
      adjValue = -Math.abs(adpAmt);
    } else if (is1099) {
      adjValue = adPlusAmt + (-adMinAmt);
    }

    let adjDisplay = "";
    if (adjValue > 0) adjDisplay = "+" + adjValue.toFixed(2);
    else if (adjValue < 0) adjDisplay = adjValue.toFixed(2);
    else adjDisplay = "0.00";

    const netW2 = (invAmt + adjValue).toFixed(2);
    const net1099 = (invAmt + adjValue).toFixed(2);

    return (
      <tr className="font-weight-bold">
        <th scope="row" colSpan={2} style={CUSTOM_REPORT_STYLES.totalCell}>Total</th>

        <td style={CUSTOM_REPORT_STYLES.totalCell}>{hrs}h {min}m</td>

        <td style={CUSTOM_REPORT_STYLES.totalCell}>{formatAmount(invAmt)}</td>

        <td style={CUSTOM_REPORT_STYLES.totalCell}>{formatAmount(adjDisplay)}</td>

        <td style={CUSTOM_REPORT_STYLES.totalCell}>{formatAmount(isW2 ? netW2 : net1099)}</td>

        <td style={CUSTOM_REPORT_STYLES.totalCell}>{formatAmount(payAmt)}</td>
      </tr>
    );
  }, [monthlyRpt, usrType, candidateEntity]);

  const tmLogRptExportPDF = useCallback(() => {
    const headers = [
      [
        "Year",
        "Month",
        "Total Hours",
        "Invoice Amount ($)",
        usrType === "candidate" && candidateEntity === "W2 Basis" ? "ADP(TAX) Adjustment" : "Adjustment",
        "Payment Amount ($)"
      ]
    ];

    const data = monthlyRpt.map((x) => {
      const monthName = months.find((m) => m.id === x.month)?.name || "N/A";
      const totalAmount = formatAmount(Number(x.totalAmount).toFixed(2));
      const paidAmount = formatAmount(Number(x.paidAmount).toFixed(2));

      let adjustmentText = "";
      if (usrType === "candidate" && candidateEntity === "W2 Basis") {
        if (x.adpDeduction) adjustmentText = `-${Number(x.adpDeduction).toFixed(2)}`;
      } else {
        if (x.adjustPlus) adjustmentText = `+${Number(x.adjustPlus).toFixed(2)}`;
        else if (x.adjustMinus) adjustmentText = `-${Number(x.adjustMinus).toFixed(2)}`;
      }

      return [
        x.year,
        monthName,
        `${x.hours}h ${x.minutes}m`,
        totalAmount,
        formatAmount(adjustmentText),
        paidAmount,
      ];
    });

    setSelectedCol(headers);
    setSelectedColData(data);
    setPdfShowHide((prev) => !prev);
  }, [monthlyRpt, months, usrType, candidateEntity]);

  const handlepdfShowHide = useCallback(() => {
    setPdfShowHide((prev) => !prev);
  }, []);

  const totalInvoiceAmount = useMemo(() => {
    return monthlyRpt.reduce(
      (sum, item) => sum + (Number(item?.totalAmount) || 0),
      0
    );
  }, [monthlyRpt]);

  const totalReceivedAmount = useMemo(() => {
    return monthlyRpt.reduce(
      (sum, item) => sum + (Number(item?.paidAmount) || 0),
      0
    );
  }, [monthlyRpt]);

  const totalHoursLabel = useMemo(() => {
    let totalMins = 0;
    monthlyRpt.forEach((item) => {
      totalMins += (Number(item.hours) || 0) * 60 + (Number(item.minutes) || 0);
    });
    const hrs = Math.floor(totalMins / 60);
    const mins = totalMins % 60;
    return `${hrs}h ${mins}m`;
  }, [monthlyRpt]);

  const totalNetAmount = useMemo(() => {
    return monthlyRpt.reduce(
      (sum, item) => sum + Number(getNetAmountWithAdjustment(item) || 0),
      0
    );
  }, [monthlyRpt, getNetAmountWithAdjustment]);

  const reportTitle = useMemo(() => {
    if (isCandidateUser) {
      const candidateName = candidateObj ? `${candidateObj.firstName} ${candidateObj.lastName}`.trim() : "";
      return candidateName ? `${candidateName} Monthly Report` : "Candidate Monthly Report";
    }
    const selectedCandidate = candidateList.find(
      (item) => String(item.performerid) === String(selectedCandidateId)
    );
    if (selectedCandidate) {
      return `${selectedCandidate.performername} ${selectedCandidate.lastName}`.trim() + " Monthly Report";
    }
    return "Candidate Monthly Report";
  }, [isCandidateUser, candidateObj, candidateList, selectedCandidateId]);

  return (
    <React.Fragment>
      <div className="col-md-12" style={CUSTOM_REPORT_STYLES.pageWrap}>
        <div style={CUSTOM_REPORT_STYLES.shell}>
          <div style={CUSTOM_REPORT_STYLES.hero}>
            <div style={CUSTOM_REPORT_STYLES.heroRow}>
              <div style={CUSTOM_REPORT_STYLES.titleRow}>
                <div style={CUSTOM_REPORT_STYLES.titleIcon}>
                  <i className="fa fa-bar-chart" aria-hidden="true"></i>
                </div>
                <div style={CUSTOM_REPORT_STYLES.titleText}>
                  <h1 style={CUSTOM_REPORT_STYLES.title}>{reportTitle}</h1>
                  <p style={CUSTOM_REPORT_STYLES.subtitle}>Filter candidate logs and export custom excel or PDF reports.</p>
                </div>
              </div>
              <div style={CUSTOM_REPORT_STYLES.actionRow}>
                <style>{`
                  #tmLogTable {
                    display: inline-flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                    gap: 8px !important;
                    border-radius: 8px !important;
                    border: 1px solid #cbd5e1 !important;
                    background-color: #ffffff !important;
                    background: #ffffff !important;
                    color: #334155 !important;
                    padding: 9px 18px !important;
                    font-size: 13px !important;
                    font-weight: 600 !important;
                    box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04) !important;
                    cursor: pointer !important;
                    transition: all 0.2s ease !important;
                    line-height: 1.5 !important;
                    margin: 0 !important;
                  }
                  #tmLogTable:hover {
                    background-color: #f8fafc !important;
                    border-color: #94a3b8 !important;
                    color: #0f172a !important;
                  }
                  #tmLogTable:disabled, #tmLogTable[disabled] {
                    opacity: 0.6 !important;
                    cursor: not-allowed !important;
                    pointer-events: none !important;
                    box-shadow: none !important;
                  }
                `}</style>
                {monthlyRpt.length > 0 ? (
                  <ReactHTMLTableToExcel
                    id="tmLogTable"
                    className="btn btn-sm"
                    table="Invoice_Report"
                    filename="Invoice_Report"
                    sheet="Invoice_Report"
                    buttonText={<><i className="fa fa-file-excel-o" style={{ marginRight: "6px", color: "#16a34a", fontSize: "14px" }} aria-hidden="true" /> Export to Excel</>}
                  />
                ) : (
                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled
                    style={{
                      ...CUSTOM_REPORT_STYLES.reportActionButton,
                      opacity: 0.6,
                      cursor: "not-allowed",
                      pointerEvents: "none",
                      boxShadow: "none"
                    }}
                  >
                    <i className="fa fa-file-excel-o" style={{ marginRight: "6px", color: "#16a34a", fontSize: "14px" }} aria-hidden="true" /> Export to Excel
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-sm"
                  disabled={monthlyRpt.length === 0}
                  onClick={tmLogRptExportPDF}
                  style={{
                    ...CUSTOM_REPORT_STYLES.reportActionButton,
                    ...(monthlyRpt.length === 0 ? { opacity: 0.6, cursor: "not-allowed", pointerEvents: "none", boxShadow: "none" } : {})
                  }}
                >
                  <i className="fa fa-file-pdf-o" style={{ marginRight: "6px", color: "#dc2626", fontSize: "14px" }} aria-hidden="true"></i> Generate PDF
                </button>
              </div>
            </div>
          </div>
          <div style={{ ...CUSTOM_REPORT_STYLES.statsGrid, padding: "18px 22px 18px 22px" }}>
              <div style={CUSTOM_REPORT_STYLES.statCard}>
                <div style={CUSTOM_REPORT_STYLES.statIcon}>
                  <i className="fa fa-table" aria-hidden="true"></i>
                </div>
                <div style={CUSTOM_REPORT_STYLES.statContent}>
                  <div style={CUSTOM_REPORT_STYLES.statLabel}>Report Rows</div>
                  <div style={CUSTOM_REPORT_STYLES.statValue}>{monthlyRpt.length}</div>
                </div>
              </div>
              <div style={CUSTOM_REPORT_STYLES.statCard}>
                <div style={CUSTOM_REPORT_STYLES.statIcon}>
                  <i className="fa fa-clock-o" aria-hidden="true"></i>
                </div>
                <div style={CUSTOM_REPORT_STYLES.statContent}>
                  <div style={CUSTOM_REPORT_STYLES.statLabel}>Total Hours</div>
                  <div style={CUSTOM_REPORT_STYLES.statValue}>{totalHoursLabel}</div>
                </div>
              </div>
              <div style={CUSTOM_REPORT_STYLES.statCard}>
                <div style={CUSTOM_REPORT_STYLES.statIcon}>
                  <i className="fa fa-usd" aria-hidden="true"></i>
                </div>
                <div style={CUSTOM_REPORT_STYLES.statContent}>
                  <div style={CUSTOM_REPORT_STYLES.statLabel}>Net Amount</div>
                  <div style={CUSTOM_REPORT_STYLES.statValue}>{formatAmount(totalNetAmount.toFixed(2))}</div>
                </div>
              </div>
              <div style={CUSTOM_REPORT_STYLES.statCard}>
                <div style={CUSTOM_REPORT_STYLES.statIcon}>
                  <i className="fa fa-check-circle-o" aria-hidden="true"></i>
                </div>
                <div style={CUSTOM_REPORT_STYLES.statContent}>
                  <div style={CUSTOM_REPORT_STYLES.statLabel}>Received Amount</div>
                  <div style={CUSTOM_REPORT_STYLES.statValue}>{formatAmount(totalReceivedAmount.toFixed(2))}</div>
                </div>
              </div>
            </div>
          <div style={CUSTOM_REPORT_STYLES.filterSection}>
            <div style={CUSTOM_REPORT_STYLES.filterShell}>
              <div style={CUSTOM_REPORT_STYLES.filterGrid}>
                {usrType !== "candidate" && (
                  <div>
                    <label htmlFor="candidate" style={CUSTOM_REPORT_STYLES.filterLabel}>Candidate</label>
                    <select
                      name="candidate"
                      id="candidate"
                      className="form-control"
                      style={CUSTOM_REPORT_STYLES.selectInput}
                      onChange={handleCandidateChange}
                      aria-label="Select Candidate"
                    >
                      <option value="">Select Candidate</option>
                      {candidateList.map((item) => (
                        <option key={item.performerid} value={item.performerid}>
                          {item.performername} {item.lastName}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <label htmlFor="year" style={CUSTOM_REPORT_STYLES.filterLabel}>Year</label>
                  <select
                    name="year"
                    id="year"
                    className="form-control"
                    style={CUSTOM_REPORT_STYLES.selectInput}
                    onChange={handleChangeYear}
                    value={selectedYear}
                    aria-label="Select Year"
                  >
                    <option value="">Select Year</option>
                    {years
                      .filter(x => x.name <= (new Date().getFullYear()))
                      .sort((a, b) => Number(b.name) - Number(a.name))
                      .map((item) => (
                        <option key={item.name} value={item.name}>{item.name}</option>
                      ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
          <div style={CUSTOM_REPORT_STYLES.body}>
            <div style={CUSTOM_REPORT_STYLES.tableWrap}>
              <div className="table-responsive">
                <table
                  id="Invoice_Report"
                  className="table align-items-center table-hover mb-0 custom-report-table"
                  style={CUSTOM_REPORT_STYLES.table}
                >
                  <thead>
                    <tr>
                      <th style={CUSTOM_REPORT_STYLES.tableHeadCell}>Year</th>
                      <th style={CUSTOM_REPORT_STYLES.tableHeadCell}>Month</th>
                      <th style={CUSTOM_REPORT_STYLES.tableHeadCell}>Total Hours</th>
                      <th style={CUSTOM_REPORT_STYLES.tableHeadCell}>Amount ($)</th>
                      <th style={CUSTOM_REPORT_STYLES.tableHeadCell}>
                        {usrType === "candidate" && candidateEntity === "W2 Basis"
                          ? "ADP (TAX) Adjustment"
                          : "Adjustment"}
                      </th>
                      <th style={CUSTOM_REPORT_STYLES.tableHeadCell}>Net Amount ($)</th>
                      <th style={CUSTOM_REPORT_STYLES.tableHeadCell}>Received Amount ($)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthlyRpt.length > 0 && monthlyRpt.map((item, index) => {
                      const monthName =
                        months.find((x) => x.id === item.month)?.name || "N/A";

                      const amount = Number(item.totalAmount).toFixed(2);
                      const paid = Number(item.paidAmount).toFixed(2);

                      const isCandidate = usrType === "candidate";
                      const isW2 = isCandidate && candidateEntity === "W2 Basis";
                      const is1099 = !isCandidate || candidateEntity === "1099 Basis";

                      let adjustmentText = "";

                      if (isW2 && item.adpDeduction) {
                        adjustmentText = `-${Number(item.adpDeduction).toFixed(2)}`;
                      } else if (is1099) {
                        if (item.adjustPlus) {
                          adjustmentText = `+${Number(item.adjustPlus).toFixed(2)}`;
                        } else if (item.adjustMinus) {
                          adjustmentText = `-${Number(item.adjustMinus).toFixed(2)}`;
                        }
                      }

                      return (
                        <tr key={`${item.year}-${item.month}-${index}`}>
                          <td style={CUSTOM_REPORT_STYLES.tableCell}>
                            <span style={CUSTOM_REPORT_STYLES.yearBadge}>{item.year}</span>
                          </td>
                          <td style={CUSTOM_REPORT_STYLES.tableCell}>{monthName}</td>
                          <td style={CUSTOM_REPORT_STYLES.tableCell}>
                            {item.hours}h {item.minutes}m
                          </td>
                          <td style={CUSTOM_REPORT_STYLES.tableCell}>{formatAmount(amount)}</td>
                          <td style={CUSTOM_REPORT_STYLES.tableCell}>{formatAmount(adjustmentText)}</td>
                          <td style={CUSTOM_REPORT_STYLES.tableCell}>{formatAmount(getNetAmountWithAdjustment(item))}</td>
                          <td style={CUSTOM_REPORT_STYLES.tableCell}>{formatAmount(paid)}</td>
                        </tr>
                      );
                    })}
                    {monthlyRpt.length > 0 && getTotalRows()}
                    {monthlyRpt.length === 0 && (
                      <tr>
                        <td colSpan={7} className="p-0">
                          <div style={CUSTOM_REPORT_STYLES.emptyState}>
                            <div style={CUSTOM_REPORT_STYLES.emptyIcon}>
                              <i className="fa fa-bar-chart" aria-hidden="true"></i>
                            </div>
                            <h4 style={CUSTOM_REPORT_STYLES.emptyTitle}>No report rows available</h4>
                            <p style={CUSTOM_REPORT_STYLES.emptyText}>
                              Select a candidate or year to load report data, or wait until monthly payment records are available for this account.
                            </p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      <GeneratePdf
        handlepdfShowHide={handlepdfShowHide}
        pdfShowHide={pdfShowHide}
        selectedCol={selectedCol}
        selectedColData={selectedColData}
      ></GeneratePdf>
    </React.Fragment>
  );
}

export default CustomReport;
